import { CustomDialogComponent } from '@theme/custom-dialog';

/**
 * Agreement Dialog Component
 *
 * 체크박스 동의 기능을 갖춘 다이얼로그 컴포넌트
 * 모든 체크박스가 선택되어야 확인 버튼이 활성화됨
 *
 * @typedef {object} AgreementDialogRefs
 * @property {HTMLDialogElement} dialog - The dialog element
 * @property {HTMLButtonElement} confirmButton - The confirm button
 *
 * @extends CustomDialogComponent
 */
export class AgreementDialogComponent extends CustomDialogComponent {
  /** @type {HTMLInputElement[]} */
  #checkboxes = [];

  /** @type {(() => void) | null} */
  #pendingCallback = null;

  /** @type {(() => Promise<void>) | undefined} */
  #parentCloseDialog;

  /** @type {string} */
  #defaultConfirmButtonLabel = '';

  connectedCallback() {
    super.connectedCallback();
    this.#initCheckboxes();
    this.#defaultConfirmButtonLabel = this.#getConfirmButton()?.textContent?.trim() || '';

    // 부모 클래스의 closeDialog를 저장하고 래핑
    this.#parentCloseDialog = this.closeDialog;
    this.closeDialog = this.#wrappedCloseDialog;
  }

  /**
   * 체크박스 초기화 및 이벤트 바인딩
   */
  #initCheckboxes() {
    this.#checkboxes = /** @type {HTMLInputElement[]} */ (
      Array.from(this.querySelectorAll('.agreement-dialog__checkbox'))
    );

    this.#checkboxes.forEach((checkbox) => {
      checkbox.addEventListener('change', this.#updateButtonState);
    });

    this.#updateButtonState();
  }

  /**
   * 체크박스 상태에 따라 버튼 활성화/비활성화
   */
  #updateButtonState = () => {
    const confirmButton = this.#getConfirmButton();
    if (!confirmButton) return;

    const allChecked = this.#checkboxes.every((cb) => cb.checked);
    /** @type {HTMLButtonElement} */ (confirmButton).disabled = !allChecked;
  };

  /**
   * 다이얼로그를 열고 콜백 저장
   * @param {() => void} callback - 확인 시 실행할 콜백
   * @param {string} [confirmButtonLabel] - 확인 버튼 문구
   */
  showWithCallback(callback, confirmButtonLabel) {
    this.#pendingCallback = callback;
    this.#setConfirmButtonLabel(confirmButtonLabel);
    this.showDialog();
  }

  /**
   * 래핑된 closeDialog - 체크박스 초기화 포함
   */
  #wrappedCloseDialog = async () => {
    if (this.#parentCloseDialog) {
      await this.#parentCloseDialog();
    }
    this.#setConfirmButtonLabel();
    this.#resetCheckboxes();
  };

  /**
   * 체크박스 상태 초기화
   */
  #resetCheckboxes() {
    this.#checkboxes.forEach((checkbox) => {
      checkbox.checked = false;
    });
    this.#updateButtonState();
    this.#pendingCallback = null;
  }

  /**
   * Returns the confirm button element.
   * @returns {HTMLButtonElement | null}
   */
  #getConfirmButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.agreement-dialog__button'));
  }

  /**
   * Sets the confirm button label, restoring the default when omitted.
   * @param {string} [label]
   */
  #setConfirmButtonLabel(label = this.#defaultConfirmButtonLabel) {
    const confirmButton = this.#getConfirmButton();
    if (!confirmButton) return;

    confirmButton.textContent = label || this.#defaultConfirmButtonLabel;
  }

  /**
   * 확인 버튼 클릭 핸들러
   * @override
   */
  handleConfirm = () => {
    // 모든 체크박스가 체크되었는지 다시 확인
    const allChecked = this.#checkboxes.every((cb) => cb.checked);
    if (!allChecked) return;

    // 커스텀 이벤트 발생
    this.dispatchEvent(
      new CustomEvent('agreement-dialog:confirmed', {
        bubbles: true,
        composed: true,
      })
    );

    // 저장된 콜백 실행
    if (this.#pendingCallback) {
      this.#pendingCallback();
    }

    this.closeDialog();
  };
}

if (!customElements.get('agreement-dialog-component')) {
  customElements.define('agreement-dialog-component', AgreementDialogComponent);
}
